import { inject, Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  finalize,
  Observable,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { HttpMainService } from '../http-main.service';
import {
  AccountResponseModel,
  BalanceResponseModel,
  ListModel,
  TraderAccountResponseModel,
  TraderResponseModel,
  WithdrawableResponseModel,
} from '@itb-monorepo/models';
import { AUTH_SCOPE_TOKEN } from '@itb-monorepo/enums';

@Injectable({
  providedIn: 'root',
})
export class TradersService {
  private readonly httpMainService = inject(HttpMainService);

  private readonly _currentTrader$ = new BehaviorSubject<
    TraderResponseModel | undefined
  >(undefined);

  readonly currentTrader$ = this._currentTrader$.asObservable();
  readonly isReady = new BehaviorSubject<boolean>(false);
  private currentTraderRequest$?: Observable<TraderResponseModel>;

  loading = false;

  private readonly scope = inject(AUTH_SCOPE_TOKEN);
  get currentTrader(): TraderResponseModel | undefined {
    return this._currentTrader$.value;
  }

  loadCurrentTrader(forceRefresh = false, context?: HttpContext): Observable<TraderResponseModel> {
    if (!forceRefresh && this.currentTrader) {
      return of(this.currentTrader);
    }

    if (this.currentTraderRequest$) {
      return this.currentTraderRequest$;
    }

    const baseUrl =
      this.scope === 'admins' ? '/member/v1/admins' : '/trade/v1/traders';

    this.loading = true;

    this.currentTraderRequest$ = this.httpMainService
      .get<TraderResponseModel>(`${baseUrl}/current`, undefined, context)
      .pipe(
        tap((trader: TraderResponseModel) => {
          this.setCurrentTrader(trader);
        }),
        finalize(() => {
          this.loading = false;
          this.currentTraderRequest$ = undefined;
        }),
        shareReplay({
          bufferSize: 1,
          refCount: false,
        })
      );

    return this.currentTraderRequest$;
  }
  fetchCurrentTrader(forceRefresh = false): void {
    this.loadCurrentTrader(forceRefresh)
      .pipe(
        catchError((error) => {
          if (!this.currentTrader) {
            this.isReady.next(false);
          }

          return of(undefined);
        })
      )
      .subscribe();
  }

  setCurrentTrader(trader: TraderResponseModel): void {
    this._currentTrader$.next(trader);
    this.isReady.next(true);
  }

  clearCurrentTrader(): void {
    this._currentTrader$.next(undefined);
    this.isReady.next(false);
  }

  returnsCurrentTrader(forceRefresh = false): Observable<TraderResponseModel> {
    return this.loadCurrentTrader(forceRefresh);
  }

  getTraderAccounts(
    identifier: string = 'current',
    wallet?: boolean
  ): Observable<ListModel<TraderAccountResponseModel>> {
    const filter = wallet ? 'is_wallet:eq(true)' : '';

    const url = `/trade/v1/traders/${identifier}/accounts${
      filter ? `?filter=${filter}` : ''
    }`;

    return this.httpMainService.get(url);
  }

  getTraderDetails(userId: string): Observable<TraderResponseModel> {
    return this.httpMainService.get(`/trade/v1/traders/${userId}`);
  }

  getAccountsList(params: any): Observable<ListModel<AccountResponseModel>> {
    const query = {
      skip: params.skip,
      take: params.take,
      include: params.include,
      filter: `platform_code:eq("${
        params.platform_code
      }"),kind:eq("${params.kind.toString().toUpperCase()}")`,
    };

    return this.httpMainService.get(
      `/trade/v1/users/${params.user_id}/accounts`,
      query
    );
  }

  getBalances(params: any): Observable<ListModel<BalanceResponseModel>> {
    return this.httpMainService.get(
      `/trade/v1/platforms/${params.platform_code.toUpperCase()}/accounts/${
        params.account_id
      }/balances`,
      params
    );
  }

  getAccountWithWithdrawable(
    params: any
  ): Observable<WithdrawableResponseModel> {
    return this.httpMainService.get(
      `/trade/v1/traders/-/platforms/${params.platform_code.toUpperCase()}/accounts/${
        params.account_id
      }`,
      params
    );
  }
}
